import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import {
  getLostItems,
  getFoundItems,
  deleteLostItem,
  deleteFoundItem,
  updateLostItem,
  updateFoundItem,
  getClaimRequests,
  updateClaimRequest
} from '../services/itemService';
import { LostItem, FoundItem, ClaimRequest } from '../types';
import { capitalize, inputClass } from '../constants';

type TabType = 'lost' | 'found' | 'claims' | 'flagged';

const AdminPage: React.FC = () => {
  const { currentUser, isAdmin, isStaff } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabType>('lost');
  const [lostItems, setLostItems] = useState<LostItem[]>([]);
  const [foundItems, setFoundItems] = useState<FoundItem[]>([]);
  const [claimRequests, setClaimRequests] = useState<ClaimRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Flag modal state
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagTarget, setFlagTarget] = useState<{ id: string; type: 'lost' | 'found' } | null>(null);
  const [flagReason, setFlagReason] = useState('');

  useEffect(() => {
    if (!isAdmin && !isStaff) {
      navigate('/');
      return;
    }
    fetchAllData();
  }, [isAdmin, isStaff, navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [lost, found, claims] = await Promise.all([
        getLostItems(),
        getFoundItems(),
        getClaimRequests()
      ]);
      setLostItems(lost);
      setFoundItems(found);
      setClaimRequests(claims);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (id: string, type: 'lost' | 'found') => {
    if (!window.confirm(`Are you sure you want to delete this ${type} item? This action cannot be undone.`)) {
      return;
    }

    setActionLoading(id);
    try {
      if (type === 'lost') {
        await deleteLostItem(id);
        setLostItems(prev => prev.filter(item => item.id !== id));
      } else {
        await deleteFoundItem(id);
        setFoundItems(prev => prev.filter(item => item.id !== id));
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const openFlagModal = (id: string, type: 'lost' | 'found') => {
    setFlagTarget({ id, type });
    setFlagReason('');
    setShowFlagModal(true);
  };

  const handleFlagItem = async () => {
    if (!flagTarget || !flagReason.trim()) return;

    setActionLoading(flagTarget.id);
    try {
      const flagData = {
        flagged: true,
        flagReason: flagReason.trim(),
        flaggedBy: currentUser?.uid,
        flaggedAt: new Date()
      };

      if (flagTarget.type === 'lost') {
        await updateLostItem(flagTarget.id, flagData);
        setLostItems(prev => prev.map(item =>
          item.id === flagTarget.id ? { ...item, ...flagData } : item
        ));
      } else {
        await updateFoundItem(flagTarget.id, flagData);
        setFoundItems(prev => prev.map(item =>
          item.id === flagTarget.id ? { ...item, ...flagData } : item
        ));
      }
      setShowFlagModal(false);
      setFlagTarget(null);
      setFlagReason('');
    } catch (error) {
      console.error('Error flagging item:', error);
      alert('Failed to flag item. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnflagItem = async (id: string, type: 'lost' | 'found') => {
    setActionLoading(id);
    try {
      const unflagData = {
        flagged: false,
        flagReason: '',
        flaggedBy: '',
        flaggedAt: undefined
      };

      if (type === 'lost') {
        await updateLostItem(id, unflagData);
        setLostItems(prev => prev.map(item =>
          item.id === id ? { ...item, flagged: false, flagReason: undefined, flaggedBy: undefined, flaggedAt: undefined } : item
        ));
      } else {
        await updateFoundItem(id, unflagData);
        setFoundItems(prev => prev.map(item =>
          item.id === id ? { ...item, flagged: false, flagReason: undefined, flaggedBy: undefined, flaggedAt: undefined } : item
        ));
      }
    } catch (error) {
      console.error('Error unflagging item:', error);
      alert('Failed to unflag item. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleClaimAction = async (claimId: string, action: 'approved' | 'rejected') => {
    setActionLoading(claimId);
    try {
      await updateClaimRequest(claimId, {
        status: action,
        reviewedBy: currentUser?.uid,
        reviewedAt: new Date()
      });
      setClaimRequests(prev => prev.map(claim =>
        claim.id === claimId ? { ...claim, status: action } : claim
      ));
    } catch (error) {
      console.error('Error updating claim:', error);
      alert('Failed to update claim. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const getDate = (item: LostItem | FoundItem) => {
    const dateField = 'dateLost' in item ? item.dateLost : item.dateFound;
    return dateField instanceof Date ? dateField : new Date((dateField as any).seconds * 1000);
  };

  const flaggedItems = [
    ...lostItems.filter(item => item.flagged).map(item => ({ ...item, itemType: 'lost' as const })),
    ...foundItems.filter(item => item.flagged).map(item => ({ ...item, itemType: 'found' as const }))
  ];

  const tabs: { key: TabType; label: string; count: number }[] = [
    { key: 'lost', label: 'Lost Items', count: lostItems.length },
    { key: 'found', label: 'Found Items', count: foundItems.length },
    { key: 'claims', label: 'Claim Requests', count: claimRequests.filter(c => c.status === 'pending').length },
    { key: 'flagged', label: 'Flagged', count: flaggedItems.length }
  ];

  if (!isAdmin && !isStaff) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-2 text-gray-600">Manage items, claims, and flagged content</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-red-600">{lostItems.length}</div>
            <div className="text-gray-600">Lost Items</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-green-600">{foundItems.length}</div>
            <div className="text-gray-600">Found Items</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-yellow-600">
              {claimRequests.filter(c => c.status === 'pending').length}
            </div>
            <div className="text-gray-600">Pending Claims</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-3xl font-bold text-orange-600">{flaggedItems.length}</div>
            <div className="text-gray-600">Flagged Items</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 sm:px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.key ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Lost Items Tab */}
            {activeTab === 'lost' && (
              <ItemsTable
                items={lostItems}
                type="lost"
                onDelete={handleDeleteItem}
                onFlag={openFlagModal}
                onUnflag={handleUnflagItem}
                actionLoading={actionLoading}
                getDate={getDate}
              />
            )}

            {/* Found Items Tab */}
            {activeTab === 'found' && (
              <ItemsTable
                items={foundItems}
                type="found"
                onDelete={handleDeleteItem}
                onFlag={openFlagModal}
                onUnflag={handleUnflagItem}
                actionLoading={actionLoading}
                getDate={getDate}
              />
            )}

            {/* Claims Tab */}
            {activeTab === 'claims' && (
              <ClaimsTable
                claims={claimRequests}
                onAction={handleClaimAction}
                actionLoading={actionLoading}
              />
            )}

            {/* Flagged Items Tab */}
            {activeTab === 'flagged' && (
              <FlaggedItemsTable
                items={flaggedItems}
                onDelete={handleDeleteItem}
                onUnflag={handleUnflagItem}
                actionLoading={actionLoading}
                getDate={getDate}
              />
            )}
          </div>
        </div>
      </div>

      {/* Flag Modal */}
      {showFlagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Flag Item for Review</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for flagging *
              </label>
              <textarea
                value={flagReason}
                onChange={(e) => setFlagReason(e.target.value)}
                className={inputClass}
                rows={3}
                placeholder="Explain why this item needs review..."
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowFlagModal(false);
                  setFlagTarget(null);
                  setFlagReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleFlagItem}
                disabled={!flagReason.trim() || actionLoading !== null}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
              >
                Flag Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Items Table Component
interface ItemsTableProps {
  items: (LostItem | FoundItem)[];
  type: 'lost' | 'found';
  onDelete: (id: string, type: 'lost' | 'found') => void;
  onFlag: (id: string, type: 'lost' | 'found') => void;
  onUnflag: (id: string, type: 'lost' | 'found') => void;
  actionLoading: string | null;
  getDate: (item: LostItem | FoundItem) => Date;
}

const ItemsTable: React.FC<ItemsTableProps> = ({
  items, type, onDelete, onFlag, onUnflag, actionLoading, getDate
}) => {
  if (items.length === 0) {
    return <p className="text-gray-500 text-center py-8">No {type} items found.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reporter</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {items.map(item => (
            <tr key={item.id} className={item.flagged ? 'bg-orange-50' : ''}>
              <td className="px-4 py-4">
                <div className="flex items-center">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt="" className="w-10 h-10 rounded object-cover mr-3" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-gray-200 mr-3 flex items-center justify-center">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <div>
                    <Link to={`/${type}/${item.id}`} className="font-medium text-gray-900 hover:text-primary-600">
                      {item.title}
                    </Link>
                    {item.flagged && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-orange-100 text-orange-800 rounded-full">
                        Flagged
                      </span>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-4 py-4 text-sm text-gray-600">{capitalize(item.category)}</td>
              <td className="px-4 py-4 text-sm text-gray-600">{item.location || 'Not specified'}</td>
              <td className="px-4 py-4 text-sm text-gray-600">{format(getDate(item), 'MMM d, yyyy')}</td>
              <td className="px-4 py-4">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  item.status === 'active' ? 'bg-blue-100 text-blue-800' :
                  item.status === 'claimed' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {capitalize(item.status)}
                </span>
              </td>
              <td className="px-4 py-4 text-sm text-gray-600">
                {'reporterEmail' in item ? item.reporterEmail : item.finderEmail}
              </td>
              <td className="px-4 py-4 text-right">
                <div className="flex justify-end gap-2">
                  {item.flagged ? (
                    <button
                      onClick={() => onUnflag(item.id, type)}
                      disabled={actionLoading === item.id}
                      className="text-green-600 hover:text-green-800 disabled:opacity-50"
                      title="Remove flag"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      onClick={() => onFlag(item.id, type)}
                      disabled={actionLoading === item.id}
                      className="text-orange-600 hover:text-orange-800 disabled:opacity-50"
                      title="Flag for review"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={() => onDelete(item.id, type)}
                    disabled={actionLoading === item.id}
                    className="text-red-600 hover:text-red-800 disabled:opacity-50"
                    title="Delete item"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Claims Table Component
interface ClaimsTableProps {
  claims: ClaimRequest[];
  onAction: (id: string, action: 'approved' | 'rejected') => void;
  actionLoading: string | null;
}

const ClaimsTable: React.FC<ClaimsTableProps> = ({ claims, onAction, actionLoading }) => {
  if (claims.length === 0) {
    return <p className="text-gray-500 text-center py-8">No claim requests found.</p>;
  }

  const getClaimDate = (claim: ClaimRequest) => {
    return claim.createdAt instanceof Date
      ? claim.createdAt
      : new Date((claim.createdAt as any).seconds * 1000);
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Claimant</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Type</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {claims.map(claim => (
            <tr key={claim.id}>
              <td className="px-4 py-4">
                <div>
                  <div className="font-medium text-gray-900">{claim.claimantName}</div>
                  <div className="text-sm text-gray-500">{claim.claimantEmail}</div>
                </div>
              </td>
              <td className="px-4 py-4">
                <Link
                  to={`/${claim.itemType}/${claim.itemId}`}
                  className="text-primary-600 hover:text-primary-800"
                >
                  View {capitalize(claim.itemType)} Item
                </Link>
              </td>
              <td className="px-4 py-4 text-sm text-gray-600 max-w-xs truncate">
                {claim.description}
              </td>
              <td className="px-4 py-4 text-sm text-gray-600">
                {format(getClaimDate(claim), 'MMM d, yyyy')}
              </td>
              <td className="px-4 py-4">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  claim.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  claim.status === 'approved' ? 'bg-green-100 text-green-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {capitalize(claim.status)}
                </span>
              </td>
              <td className="px-4 py-4 text-right">
                {claim.status === 'pending' && (
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => onAction(claim.id, 'approved')}
                      disabled={actionLoading === claim.id}
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => onAction(claim.id, 'rejected')}
                      disabled={actionLoading === claim.id}
                      className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Flagged Items Table Component
interface FlaggedItemsTableProps {
  items: ((LostItem | FoundItem) & { itemType: 'lost' | 'found' })[];
  onDelete: (id: string, type: 'lost' | 'found') => void;
  onUnflag: (id: string, type: 'lost' | 'found') => void;
  actionLoading: string | null;
  getDate: (item: LostItem | FoundItem) => Date;
}

const FlaggedItemsTable: React.FC<FlaggedItemsTableProps> = ({
  items, onDelete, onUnflag, actionLoading, getDate
}) => {
  if (items.length === 0) {
    return <p className="text-gray-500 text-center py-8">No flagged items.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flag Reason</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {items.map(item => (
            <tr key={item.id} className="bg-orange-50">
              <td className="px-4 py-4">
                <div className="flex items-center">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt="" className="w-10 h-10 rounded object-cover mr-3" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-gray-200 mr-3 flex items-center justify-center">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <Link to={`/${item.itemType}/${item.id}`} className="font-medium text-gray-900 hover:text-primary-600">
                    {item.title}
                  </Link>
                </div>
              </td>
              <td className="px-4 py-4">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  item.itemType === 'lost' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                }`}>
                  {capitalize(item.itemType)}
                </span>
              </td>
              <td className="px-4 py-4 text-sm text-gray-600 max-w-xs">
                {item.flagReason || 'No reason provided'}
              </td>
              <td className="px-4 py-4 text-sm text-gray-600">
                {format(getDate(item), 'MMM d, yyyy')}
              </td>
              <td className="px-4 py-4 text-right">
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => onUnflag(item.id, item.itemType)}
                    disabled={actionLoading === item.id}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    Clear Flag
                  </button>
                  <button
                    onClick={() => onDelete(item.id, item.itemType)}
                    disabled={actionLoading === item.id}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminPage;